import React, { useState, useEffect } from 'react';
import { FiPlus, FiTruck, FiEdit, FiTrash2, FiX, FiSearch, FiPlusCircle, FiCalendar, FiPhone, FiMessageCircle, FiInfo } from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../services/api';
import Spinner from '../components/Spinner';
import ConfirmModal from '../components/ConfirmModal';


const AddSupplierModal = ({ isOpen, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  if (!isOpen) return null;

  const handleSave = () => {
    if (name) {
      onSave({ name, phone });
      setName('');
      setPhone('');
    } else {
      toast.error("Le nom du fournisseur est obligatoire.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm">
        <h3 className="text-xl font-semibold mb-4">Ajouter un nouveau fournisseur</h3>
        <label className="block text-sm font-medium text-gray-700 mb-1">Nom du fournisseur</label>
        <input type="text" placeholder="Nom du fournisseur" value={name} onChange={(e) => setName(e.target.value)} className="w-full p-2 border rounded mb-4" />
        <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
        <input type="text" placeholder="Téléphone (optionnel)" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full p-2 border rounded mb-4" />
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg">Annuler</button>
          <button onClick={handleSave} className="bg-brand-green hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg">Enregistrer</button>
        </div>
      </div>
    </div>
  );
};

const EditSupplierModal = ({ isOpen, onClose, onSave, supplier }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: ''
  });

  useEffect(() => {
    if (supplier) {
      setFormData({
        name: supplier.name || '',
        phone: supplier.phone || '',
        address: supplier.address || ''
      });
    }
  }, [supplier]);

  if (!isOpen) return null;

  const handleSave = () => {
    if (formData.name) {
      onSave({ ...formData, _id: supplier._id });
    } else {
      toast.error("Le nom du fournisseur est obligatoire.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Modifier le fournisseur</h3>
          <button onClick={onClose}><FiX size={24} /></button>
        </div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Nom du fournisseur</label>
        <input type="text" placeholder="Nom du fournisseur" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full p-2 border rounded mb-4" />
        <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
        <input type="text" placeholder="Téléphone (optionnel)" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full p-2 border rounded mb-4" />
        <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
        <input type="text" placeholder="Adresse (optionnel)" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="w-full p-2 border rounded mb-4" />
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg">Annuler</button>
          <button onClick={handleSave} className="bg-brand-green hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg">Enregistrer</button>
        </div>
      </div>
    </div>
  );
};

const AddPurchaseModal = ({ isOpen, onClose, onSave, supplier, products }) => {
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [amountPaid, setAmountPaid] = useState('');
  const [dueDate, setDueDate] = useState('');

  useEffect(() => {
    if (isOpen) {
      setCart([]);
      setSearchTerm('');
      setAmountPaid('');
      setDueDate('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const addToCart = (product) => {
    setCart(prevCart => {
      const existing = prevCart.find(item => item._id === product._id);
      if (existing) {
        return prevCart.map(item =>
          item._id === product._id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prevCart, { ...product, quantity: 1, currentPurchasePrice: product.purchasePrice }];
    });
  };

  const updateCartItem = (productId, field, value) => {
    const numericValue = parseFloat(value);
    setCart(prevCart =>
      prevCart.map(item =>
        item._id === productId ? { ...item, [field]: isNaN(numericValue) ? 0 : numericValue } : item
      )
    );
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item._id !== productId));
  };

  const totalAmount = cart.reduce((acc, item) => acc + (item.currentPurchasePrice || 0) * (item.quantity || 0), 0);

  const handleSave = () => {
    if (cart.length === 0) {
      toast.warn("Veuillez ajouter au moins un produit à l'achat.");
      return;
    }

    const finalAmountPaid = amountPaid === '' ? totalAmount : parseFloat(amountPaid);

    const purchaseData = {
      supplierId: supplier._id,
      items: cart.map(item => ({
        productId: item._id,
        quantity: item.quantity,
        purchasePrice: item.currentPurchasePrice,
      })),
      amountPaid: finalAmountPaid,
      isCredit: finalAmountPaid < totalAmount,
      dueDate: (finalAmountPaid < totalAmount && dueDate) ? dueDate : null,
    };
    onSave(purchaseData);
  };

  const filteredProducts = products.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-5xl sm:rounded-xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b bg-white z-10">
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-800">Nouvel Achat</h3>
            <p className="text-sm text-gray-500">{supplier?.name}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors"><FiX size={24} className="text-gray-600" /></button>
        </div>

        <div className="flex flex-col md:flex-row flex-grow overflow-hidden">
          {/* Colonne Gauche : Produits */}
          <div className="flex flex-col md:w-3/5 h-[50%] md:h-full border-b md:border-b-0 md:border-r bg-white">
            <div className="p-3 border-b">
              <div className="relative">
                <FiSearch className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400" />
                <input 
                  type="text" 
                  placeholder="Rechercher un produit..." 
                  value={searchTerm} 
                  onChange={e => setSearchTerm(e.target.value)} 
                  className="w-full bg-gray-100 border border-gray-200 rounded-lg py-2.5 pl-10 pr-4 focus:outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue transition-all" 
                />
              </div>
            </div>
            <div className="overflow-y-auto flex-grow p-2 space-y-2">
              {filteredProducts.length > 0 ? filteredProducts.map(p => (
                <div key={p._id} onClick={() => addToCart(p)} className="flex justify-between items-center p-3 rounded-lg border border-gray-100 hover:border-brand-blue hover:bg-blue-50 cursor-pointer transition-all group">
                  <div>
                    <p className="font-medium text-gray-800">{p.name}</p>
                    <p className="text-xs text-gray-500">Stock: {p.quantity}</p>
                  </div>
                  <FiPlusCircle className="text-gray-400 group-hover:text-brand-blue w-6 h-6 transition-colors" />
                </div>
              )) : (
                <div className="text-center py-8 text-gray-500">Aucun produit trouvé.</div>
              )}
            </div>
          </div>

          {/* Colonne Droite : Panier & Paiement */}
          <div className="flex flex-col md:w-2/5 h-[50%] md:h-full bg-gray-50">
            <div className="p-3 bg-gray-100 border-b flex justify-between items-center">
              <h4 className="font-semibold text-gray-700 flex items-center"><FiTruck className="mr-2"/> Panier ({cart.length})</h4>
              <span className="text-sm font-bold text-brand-blue bg-blue-100 px-2 py-1 rounded">{totalAmount.toLocaleString('fr-FR')} F</span>
            </div>
            
            <div className="overflow-y-auto flex-grow p-3 space-y-3">
              {cart.length > 0 ? cart.map(item => (
                <div key={item._id} className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-medium text-sm text-gray-800 line-clamp-1">{item.name}</span>
                    <button onClick={() => removeFromCart(item._id)} className="text-gray-400 hover:text-red-500 transition-colors"><FiX size={16} /></button>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <label className="text-[10px] text-gray-500 uppercase font-bold">Qté</label>
                      <input type="number" value={item.quantity} onChange={e => updateCartItem(item._id, 'quantity', e.target.value)} className="w-full p-1 text-sm border rounded focus:border-brand-blue outline-none" min="1" />
                    </div>
                    <div className="flex-[1.5]">
                      <label className="text-[10px] text-gray-500 uppercase font-bold">Prix Achat</label>
                      <input type="number" value={item.currentPurchasePrice} onChange={e => updateCartItem(item._id, 'currentPurchasePrice', e.target.value)} className="w-full p-1 text-sm border rounded focus:border-brand-blue outline-none" min="0" />
                    </div>
                    <div className="flex-1 text-right">
                       <label className="text-[10px] text-gray-500 uppercase font-bold block">&nbsp;</label>
                       <span className="text-sm font-semibold text-gray-700">{(item.quantity * item.currentPurchasePrice).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="h-full flex flex-col items-center justify-center text-gray-400 text-sm">
                  <FiTruck className="w-8 h-8 mb-2 opacity-50" />
                  <p>Le panier est vide</p>
                </div>
              )}
            </div>

            {/* Footer Paiement */}
            <div className="p-4 bg-white border-t shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-10">
              <div className="flex justify-between items-center mb-3">
                <span className="text-gray-600 font-medium">Total à payer</span>
                <span className="text-xl font-bold text-brand-blue">{totalAmount.toLocaleString('fr-FR')} CFA</span>
              </div>
              
              <div className="mb-4">
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Montant versé (Acompte)</label>
                <div className="flex gap-2">
                  <input 
                    type="number" 
                    value={amountPaid} 
                    onChange={(e) => setAmountPaid(e.target.value)} 
                    placeholder="0"
                    className="flex-grow p-2 border rounded-lg focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none transition-all" 
                  />
                  <button 
                    onClick={() => setAmountPaid(totalAmount.toString())}
                    className="bg-blue-50 hover:bg-blue-100 text-brand-blue px-3 py-2 rounded-lg text-sm font-bold transition-colors whitespace-nowrap border border-blue-200"
                  >
                    Tout payer
                  </button>
                </div>
                {(amountPaid !== '' && parseFloat(amountPaid) < totalAmount) ? (
                  <div className="mt-3 animate-fade-in">
                    <p className="text-xs text-red-500 font-medium flex items-center mb-2">
                        <span className="w-2 h-2 rounded-full bg-red-500 mr-1"></span>
                        Reste (Dette) : {(totalAmount - (parseFloat(amountPaid) || 0)).toLocaleString('fr-FR')} CFA
                    </p>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Date d'échéance (Optionnel)</label>
                    <div className="relative">
                        <FiCalendar className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400" />
                        <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full p-2 pl-9 border rounded-lg text-sm focus:border-brand-blue outline-none" />
                    </div>
                  </div>
                ) : (
                    <p className="text-xs text-gray-400 mt-2 italic flex items-center">
                        <FiInfo className="mr-1"/> Saisissez un montant inférieur au total pour définir une échéance.
                    </p>
                )}
              </div>

              <button 
                onClick={handleSave} 
                className="w-full bg-brand-blue hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg shadow-md hover:shadow-lg transition-all transform active:scale-95 flex justify-center items-center"
              >
                <FiTruck className="mr-2" />
                Valider l'achat
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/suppliers');
      setSuppliers(response.data);
    } catch (error) {
      toast.error("Suivez vos achats et contacts fournisseurs. Cette fonctionnalité est disponible dans le plan Pro.", { icon: "🚚" });
    } finally {
      setLoading(false);
    }
  };
  
  const fetchProducts = async () => {
    try {
      const response = await api.get('/products');
      setProducts(response.data);
    } catch (error) {
      toast.error("Impossible de charger les produits pour l'achat.");
    }
  };

  useEffect(() => {
    fetchSuppliers();
    fetchProducts();
  }, []);

  const handleSaveSupplier = async (supplierData) => {
    try {
      await api.post('/suppliers', supplierData);
      toast.success(`Fournisseur "${supplierData.name}" ajouté avec succès !`);
      fetchSuppliers();
    } catch (error) {
      toast.error("Erreur lors de l'ajout du fournisseur.");
    }
    setIsAddModalOpen(false);
  };

  const handleOpenEditModal = (supplier) => {
    setSelectedSupplier(supplier);
    setIsEditModalOpen(true);
  };

  const handleUpdateSupplier = async (supplierData) => {
    try {
      await api.put(`/suppliers/${supplierData._id}`, supplierData);
      toast.success(`Fournisseur "${supplierData.name}" mis à jour !`);
      fetchSuppliers();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du fournisseur.");
    }
    setIsEditModalOpen(false);
    setSelectedSupplier(null);
  };

  const handleDeleteRequest = (supplier) => {
    setSupplierToDelete(supplier);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!supplierToDelete) return;
    try {
      await api.delete(`/suppliers/${supplierToDelete._id}`);
      toast.success(`Fournisseur "${supplierToDelete.name}" supprimé avec succès !`);
      fetchSuppliers();
    } catch (error) {
      toast.error("Impossible de supprimer le fournisseur.");
    } finally {
      setIsConfirmModalOpen(false);
      setSupplierToDelete(null);
    }
  };

  const handleOpenPurchaseModal = (supplier) => {
    setSelectedSupplier(supplier);
    setIsPurchaseModalOpen(true);
  };

  const handleSavePurchase = async (purchaseData) => {
    try {
      await api.post('/purchases', purchaseData);
      toast.success("Achat enregistré avec succès ! Le stock a été mis à jour.");
      setIsPurchaseModalOpen(false);
      // On pourrait aussi rafraîchir la liste des produits si on affichait leur stock ici
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement de l'achat.");
    }
  };

  const filteredSuppliers = suppliers.filter(supplier => 
    supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (supplier.phone && supplier.phone.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return <Spinner message="Chargement des fournisseurs..." />;
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6 gap-4">
        <h2 className="text-2xl font-semibold text-gray-800">Fournisseurs</h2>
        <button onClick={() => setIsAddModalOpen(true)} className="w-full sm:w-auto justify-center bg-brand-green hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg flex items-center transition-colors duration-200 text-sm sm:text-base">
          <FiPlus className="mr-2" />
          Ajouter un fournisseur
        </button>
      </div>

      {/* Barre de recherche */}
      <div className="mb-6 relative">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <FiSearch className="text-gray-400 h-5 w-5" />
        </div>
        <input 
            type="text" 
            placeholder="Rechercher un fournisseur par nom ou téléphone..." 
            className="w-full pl-11 pr-4 py-4 rounded-2xl bg-white border border-gray-100 shadow-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Vue Mobile (Cartes) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:hidden">
        {filteredSuppliers.map((supplier) => (
          <div key={supplier._id} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between hover:shadow-md transition-all duration-200">
            <div>
              <h3 className="font-bold text-gray-800 mb-1">{supplier.name}</h3>
              <p className="text-sm text-gray-500 mb-3">{supplier.phone || 'Pas de téléphone'}</p>
              <div className="text-xs space-y-1">
                <p>Total Acheté: <span className="font-semibold text-gray-800">{(supplier.totalPurchased || 0).toLocaleString('fr-FR')} CFA</span></p>
                <p>Dette Actuelle: <span className="font-bold text-red-600">{(supplier.currentDebt || 0).toLocaleString('fr-FR')} CFA</span></p>
              </div>
            </div>
            <div className="flex justify-end space-x-3 border-t pt-3 mt-3">
              {supplier.phone && (
                <>
                    <a href={`tel:${supplier.phone}`} className="text-blue-600 hover:text-blue-700 p-3 bg-blue-50 hover:bg-blue-100 rounded-full transition-colors" title="Appeler">
                        <FiPhone className="w-5 h-5" />
                    </a>
                    <a href={`https://wa.me/${supplier.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 p-3 bg-green-50 hover:bg-green-100 rounded-full transition-colors" title="WhatsApp">
                        <FiMessageCircle className="w-5 h-5" />
                    </a>
                </>
              )}
              <button onClick={() => handleOpenEditModal(supplier)} className="text-gray-500 hover:text-brand-blue p-2 bg-gray-100 rounded-full" title="Modifier">
                <FiEdit className="w-4 h-4" />
              </button>
              <button onClick={() => handleDeleteRequest(supplier)} className="text-gray-500 hover:text-red-500 p-2 bg-gray-100 rounded-full" title="Supprimer">
                <FiTrash2 className="w-4 h-4" />
              </button>
              <button onClick={() => handleOpenPurchaseModal(supplier)} className="text-gray-500 hover:text-brand-green p-2 bg-gray-100 rounded-full" title="Enregistrer un achat">
                <FiTruck className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white shadow-sm rounded-2xl border border-gray-100 overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="min-w-full leading-normal">
            <thead className="sticky top-0 z-10">
              <tr className="border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <th className="px-5 py-3">Nom du Fournisseur</th>
                <th className="px-5 py-3">Téléphone</th>
                <th className="px-5 py-3 text-right">Total Acheté</th>
                <th className="px-5 py-3 text-right">Dette Actuelle</th>
                <th className="px-5 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSuppliers.length > 0 ? filteredSuppliers.map((supplier) => (
                <tr key={supplier._id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="px-5 py-4 text-sm">
                    <p className="text-gray-900 whitespace-no-wrap font-semibold">{supplier.name}</p>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <div className="flex items-center">
                        <span className="text-gray-900 font-medium whitespace-no-wrap mr-3">{supplier.phone || '-'}</span>
                        {supplier.phone && (
                            <div className="flex space-x-2">
                                <a href={`tel:${supplier.phone}`} className="text-blue-600 bg-blue-50 hover:bg-blue-100 p-2 rounded-lg transition-colors" title="Appeler"><FiPhone size={16}/></a>
                                <a href={`https://wa.me/${supplier.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-green-600 bg-green-50 hover:bg-green-100 p-2 rounded-lg transition-colors" title="WhatsApp"><FiMessageCircle size={16}/></a>
                            </div>
                        )}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <p className="text-gray-900 whitespace-no-wrap font-semibold text-right">{(supplier.totalPurchased || 0).toLocaleString('fr-FR')} CFA</p>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <p className="text-red-600 whitespace-no-wrap font-bold text-right">{(supplier.currentDebt || 0).toLocaleString('fr-FR')} CFA</p>
                  </td>
                  <td className="px-5 py-4 text-sm text-center">
                    <div className="flex items-center justify-center space-x-3">
                      <button onClick={() => handleOpenEditModal(supplier)} className="text-gray-500 hover:text-brand-blue" title="Modifier"><FiEdit className="w-5 h-5" /></button>
                      <button onClick={() => handleDeleteRequest(supplier)} className="text-gray-500 hover:text-red-500" title="Supprimer"><FiTrash2 className="w-5 h-5" /></button>
                      <button onClick={() => handleOpenPurchaseModal(supplier)} className="text-gray-500 hover:text-brand-green" title="Enregistrer un achat"><FiTruck className="w-5 h-5" /></button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="5" className="text-center py-10 text-gray-500">Aucun fournisseur enregistré.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <AddSupplierModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onSave={handleSaveSupplier} />
      <EditSupplierModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} onSave={handleUpdateSupplier} supplier={selectedSupplier} />
      <AddPurchaseModal isOpen={isPurchaseModalOpen} onClose={() => setIsPurchaseModalOpen(false)} onSave={handleSavePurchase} supplier={selectedSupplier} products={products} />
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Supprimer le fournisseur"
        message={`Êtes-vous sûr de vouloir supprimer "${supplierToDelete?.name}" ? Cette action est irréversible.`}
      />
    </div>
  );
};

export default Suppliers;